/**
 * Convertir una imagen a WebP **en el navegador**, antes de subirla
 * -----------------------------------------------------------------------------
 * El servidor ya convierte, y va a seguir haciéndolo: nada de lo que llega del
 * navegador se cree. Pero en un alojamiento compartido convertir allí es lo
 * caro, y por dos motivos que no se arreglan con más código:
 *
 * 1. **La memoria.** GD descomprime la imagen entera a mapa de bits: ancho por
 *    alto por cuatro bytes. Una foto de móvil de 50 megapíxeles son 200 MB solo
 *    para abrirla, más el lienzo del reescalado. El límite de un compartido
 *    suele estar en 128 o 256, y quedarse sin memoria en PHP **no se puede
 *    atrapar**: mata la petición sin mensaje. Pantalla en blanco y a adivinar.
 *
 * 2. **La subida.** Veinticinco megas por ocho fotos son doscientos por
 *    formulario, contra un `post_max_size` que casi siempre es menor. El envío
 *    se rechaza entero y tampoco hay forma de decir por qué.
 *
 * Convirtiendo aquí, lo que viaja son unos cientos de kilobytes y el servidor
 * recibe un WebP ya pequeño: lo valida y lo vuelve a escribir sin acercarse a
 * ningún límite. El original nunca sale del ordenador de quien sube.
 *
 * ## Si no se puede, no pasa nada
 *
 * Un navegador que no sepa escribir WebP -o una imagen que no se deje leer-
 * devuelve el fichero **tal cual**, y lo convierte el servidor como hasta
 * ahora. Es una mejora del camino normal, no un requisito.
 *
 * ## La orientación
 *
 * Las fotos de móvil vienen derechas con una nota EXIF que dice cuánto girarlas.
 * Al pasarlas por un lienzo esa nota se pierde, así que se aplica al decodificar
 * -`imageOrientation`- o la foto se guarda tumbada.
 */

/** Lo que se conserva de la calidad. El mismo número que usa el servidor. */
const CALIDAD = 0.8;

/** El lado mayor. El mismo número que usa el servidor. */
const LADO_MAXIMO = 2000;

let puede = null;

/**
 * ¿Sabe este navegador **escribir** WebP?
 *
 * Leerlo lo sabe casi cualquiera; escribirlo desde un lienzo es lo que falta en
 * los Safari viejos. Se pregunta una sola vez: crea un lienzo cada vez que se
 * llama.
 *
 * @returns {boolean}
 */
export function sePuedeConvertir() {
  if (puede !== null) return puede;

  try {
    const lienzo = document.createElement('canvas');
    lienzo.width = 1;
    lienzo.height = 1;
    puede = lienzo.toDataURL('image/webp').indexOf('data:image/webp') === 0;
  } catch (error) {
    puede = false;
  }

  return puede;
}

/**
 * La imagen decodificada, con la orientación EXIF ya aplicada.
 *
 * @param {File} fichero
 * @returns {Promise<ImageBitmap|HTMLImageElement>}
 */
async function decodificar(fichero) {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(fichero, { imageOrientation: 'from-image' });
    } catch (error) {
      // Los que no entienden las opciones tiran aquí. Se intenta sin ellas: hoy
      // los navegadores ya enderezan por su cuenta al decodificar.
      try {
        return await createImageBitmap(fichero);
      } catch (otro) {
        /* al camino de abajo */
      }
    }
  }

  return await porEtiqueta(fichero);
}

/**
 * Decodificar con una `<img>`, para quien no tenga `createImageBitmap`.
 *
 * @param {File} fichero
 * @returns {Promise<HTMLImageElement>}
 */
function porEtiqueta(fichero) {
  return new Promise((bien, mal) => {
    const direccion = URL.createObjectURL(fichero);
    const imagen = new Image();

    imagen.onload = () => {
      URL.revokeObjectURL(direccion);
      bien(imagen);
    };
    imagen.onerror = () => {
      URL.revokeObjectURL(direccion);
      mal(new Error('no se pudo leer la imagen'));
    };
    imagen.src = direccion;
  });
}

/**
 * El mismo nombre, con la extensión que de verdad tiene lo que se manda.
 *
 * Se conserva el nombre que le puso quien sube -es como reconoce su foto- y se
 * cambia solo el final: decir `.png` de unos bytes WebP es mentir en la
 * descarga.
 *
 * @param {string} nombre
 * @returns {string}
 */
export function conExtensionWebp(nombre) {
  const limpio = String(nombre || 'foto').replace(/\.[^.\/]*$/, '');

  return (limpio || 'foto') + '.webp';
}

/**
 * El fichero convertido a WebP, o **el mismo** si no se pudo o no compensa.
 *
 * @param {File} fichero
 * @param {{ladoMaximo?: number, calidad?: number}} [opciones]
 * @returns {Promise<File>}
 */
export async function aWebp(fichero, opciones = {}) {
  const ladoMaximo = opciones.ladoMaximo || LADO_MAXIMO;
  const calidad = opciones.calidad || CALIDAD;

  if (!fichero || String(fichero.type || '').indexOf('image/') !== 0) return fichero;
  if (!sePuedeConvertir()) return fichero;

  let mapa = null;

  try {
    mapa = await decodificar(fichero);

    const anchoOriginal = mapa.width || mapa.naturalWidth;
    const altoOriginal = mapa.height || mapa.naturalHeight;

    if (!anchoOriginal || !altoOriginal) return fichero;

    const escala = Math.min(1, ladoMaximo / Math.max(anchoOriginal, altoOriginal));
    const ancho = Math.max(1, Math.round(anchoOriginal * escala));
    const alto = Math.max(1, Math.round(altoOriginal * escala));

    const lienzo = document.createElement('canvas');
    lienzo.width = ancho;
    lienzo.height = alto;

    // `alpha: true` y sin pintar el fondo: un lienzo recién creado es
    // transparente, y rellenarlo de blanco -que es el error de siempre- le
    // quita la transparencia a los PNG con fondo.
    const pincel = lienzo.getContext('2d', { alpha: true });

    if (!pincel) return fichero;

    pincel.drawImage(mapa, 0, 0, ancho, alto);

    const trozo = await new Promise((listo) => {
      lienzo.toBlob(listo, 'image/webp', calidad);
    });

    if (!trozo || trozo.type !== 'image/webp') return fichero;

    // Si no hubo que encoger y encima sale más grande, no compensa: se manda el
    // original y que lo convierta el servidor. Pero si hubo que encoger, se
    // manda el convertido aunque pese más, que el motivo era el tamaño.
    if (escala >= 1 && trozo.size >= fichero.size) return fichero;

    return new File([trozo], conExtensionWebp(fichero.name), {
      type: 'image/webp',
      lastModified: fichero.lastModified || undefined,
    });
  } catch (error) {
    // Cualquier tropiezo -memoria, un fichero a medias, un formato que el
    // navegador dice conocer y no- deja el original. Lo convierte el servidor.
    return fichero;
  } finally {
    if (mapa && typeof mapa.close === 'function') mapa.close();
  }
}

export default aWebp;
